import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import { NavigationBar } from "components/base/base";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { DatePicker } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { GENERATE_DOCUMENT } from "graphql/mutations";
import { useModals } from "ModalProvider";
import moment from "moment";
import { createContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import http from "utils/http";
import AssessmentBaseInfoView from "./AssessmentBaseInfoView";
import AssessmentExtraInfoView from "./AssessmentExtraInfoView";
import AssessmentStatusChain from "./AssessmentStatus";
import AssessmentStatusNoteView from "./AssessmentStatusNoteView";
import AssessmentSummaryView from "./AssessmentSummaryView";
import CategoryQuestions from "./CategoryQuestions";
import ConfirmAssessmentQuestionView from "./ConfirmAssessmentQuestionView";
import { FETCH_ASSESSMENT, SAVE_ASSESSMENT } from "./graphql";
import InternalNoteForm from "./InternalNoteForm";
import QuestionFilters from "./QuestionFilters";
import SupplierUserInfoView from "./SupplierUserInfoView";

export const AssessmentQuestionContext = createContext({});

const AssessmentDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { loading, error, data } = useQuery(FETCH_ASSESSMENT, {
    variables: { id },
  });
  const [generateDocument] = useMutation(GENERATE_DOCUMENT, {
    onError: (error) => Alert("error", error.message),
  });

  const assessmentQuestionId = searchParams.get("assessmentQuestionId");

  const [saveAssessment, saveAssessmentRes] = useMutation(SAVE_ASSESSMENT, {
    onCompleted() {
      Alert("success", "Assessment saved.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (data && assessmentQuestionId) {
      setTimeout(() => {
        const element = document.getElementById(assessmentQuestionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
    }
  }, [data, assessmentQuestionId]);

  const [filter, setFilter] = useState("all");

  const confirmModal = useModals();
  const internalNoteModal = useModals();

  function confirmAssessmentQuestion(assessmentQuestion) {
    confirmModal.present({
      title: "Confirm",
      center: true,
      maxWidth: "max-w-3xl",
      children: (
        <ConfirmAssessmentQuestionView
          assessmentQuestion={assessmentQuestion}
          hide={confirmModal.hide}
        />
      ),
    });
  }

  function showInternalNoteForm(assessmentQuestion) {
    internalNoteModal.present({
      title: "Internal Note",
      center: true,
      maxWidth: "max-w-3xl",
      children: (
        <InternalNoteForm
          assessmentQuestion={assessmentQuestion}
          hide={internalNoteModal.hide}
        />
      ),
    });
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const questions = data.assessment.questions.edges.map(({ node }) => node);
  function getAllCategoryQuestions(category) {
    let result = [];
    if (category.children) {
      category.children.edges.forEach(({ node }) => {
        result = result.concat(getAllCategoryQuestions(node));
      });
    }

    result = result.concat(
      questions.filter((question) => {
        return question.question.category.id === category.id;
      }),
    );

    return result;
  }

  function buildCategoriesTree(category) {
    let result = [];
    if (category.children) {
      category.children.edges.forEach(({ node }) => {
        result.push({
          ...node,
          children: buildCategoriesTree(node),
          questions: getAllCategoryQuestions(node).filter((i) => {
            if (filter === "all") return true;
            if (filter === "score_a") return i.score === "A";
            if (filter === "score_b") return i.score === "B";
            if (filter === "score_c") return i.score === "C";
            if (filter === "score_d") return i.score === "D";
            if (filter === "score_no") return i.score === null;
            if (filter === "required_improvement") return i.requiredImprovement;
            if (filter === "required_improvement_not_completed")
              return i.requiredImprovement && i.improvementCompleted === false;
            if (filter === "required_improvement_completed")
              return i.requiredImprovement && i.improvementCompleted === true;
            if (filter === "received_improvement")
              return i.improvements.edges.length > 0;
            if (filter === "not_required_improvement")
              return !i.requiredImprovement;
            if (filter === "follow_up_next_time") return i.followUpNextTime;
            if (filter === "follow_up_this_time")
              return i.prevCategoryAssessmentQuestion;
            if (filter === "confirm_required")
              return i.question.confirmRequired;
            if (filter === "check_docs_required")
              return i.question.checkDocsRequired;
            if (filter === "check_environment_required")
              return i.question.checkEnvironmentRequired;
            if (filter === "check_with_workers_required")
              return i.question.checkWithWorkersRequired;
            if (filter === "check_with_owner_required")
              return i.question.checkWithOwnerRequired;
            return false;
          }),
        });
      });
    }
    return result;
  }

  if (!data.assessment.category)
    return (
      <Page title="Assessment Detail" backTo="./..">
        <div className="p-6">
          This assessment does not have a category, data might be broken, please
          contact support team.
        </div>
      </Page>
    );

  const categories = buildCategoriesTree(data.assessment.category);

  async function downloadAssessment() {
    try {
      setDownloading(true);
      const name = `${data.assessment.supplier.name} - ${data.assessment.category.name}`;
      const res = await generateDocument({
        variables: {
          name,
          docType: "assessment",
          data: JSON.stringify({ id: data.assessment.id }),
        },
      });
      http
        .get(
          `${process.env.REACT_APP_SERVER_BASE_URL}generated-document/${res.data.generateDocument.document.id}/?pdf=1`,
          {
            responseType: "blob",
            withCredentials: true,
          },
        )
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name + ".pdf");
          document.body.appendChild(link);
          link.click();
          setDownloading(false);
        });
    } catch (error) {
      Alert("error", error.message);
      setDownloading(false);
    }
  }

  const baseInfo = data.assessment.baseInfo
    ? JSON.parse(data.assessment.baseInfo)
    : {};

  return (
    <AssessmentQuestionContext.Provider
      value={{
        confirmAssessmentQuestion,
        showInternalNoteForm,
        assessmentStatus: data.assessment.status,
      }}
    >
      <NavigationBar
        title={`${data.assessment.supplier.name}${baseInfo ? `(${baseInfo.name})` : ""} - ${data.assessment.category.name}`}
        rightButtons={
          <div className="flex space-x-6">
            <Button loading={downloading} onClick={downloadAssessment}>
              Download
            </Button>

            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL}assessment/assessment/${atob(id).split(":")[1]}`}
              target="_blank"
              rel="noreferrer"
            >
              Admin
            </a>
          </div>
        }
      />

      <div className="m-6">
        <AssessmentStatusChain status={data.assessment.status} />
      </div>

      <div className="m-6 grid grid-cols-12 gap-6 leading-relaxed">
        <div className="col-span-full 2xl:col-span-9 space-y-6">
          <AssessmentBaseInfoView data={data.assessment.baseInfo} />
          <div className="card px-6 py-4">
            <div className="border-b dark:border-gray-700 pb-4">
              <div className="flex justify-between items-center">
                <h4>Questions</h4>
                <QuestionFilters filter={filter} setFilter={setFilter} />
              </div>

              <div className="opacity-70 mt-1 text-xs">
                We can edit the question answers directly here for convince. But
                these questions are supposed to be answered by suppliers.
              </div>
            </div>

            <div>
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="py-6 border-gray-100 dark:border-gray-700 "
                >
                  <h2>
                    {category.indexNumber}. {category.name}
                  </h2>
                  {category.requiredSupplier ? null : (
                    <div className="opacity-70">
                      This section does not required supplier to fill in, we
                      could just write the content by ourselves.
                    </div>
                  )}
                  <div className="mt-2">{category.description}</div>
                  <CategoryQuestions
                    category={category}
                    requiredSupplier={category.requiredSupplier}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-full 2xl:col-span-3 space-y-6">
          <SupplierUserInfoView supplier={data.assessment.supplier} />

          <AssessmentStatusNoteView
            id={id}
            status={data.assessment.status}
            categories={categories}
          />

          <div className="card px-6 py-4 relative">
            <h5>Due Date</h5>
            <div className="mt-2">
              <DatePicker
                value={data.assessment.dueDate}
                onDayChange={(day) => {
                  saveAssessment({
                    variables: {
                      id,
                      dueDate: moment(day).format("YYYY-MM-DD"),
                    },
                  });
                }}
                disabled={saveAssessment.loading}
              />
            </div>
            <div className="mt-2 opacity-70 text-xs">
              Supplier is expected to complete this self assessment in time.
            </div>
          </div>

          <AssessmentExtraInfoView
            assessment={data.assessment}
            saveAssessment={saveAssessment}
            saveAssessmentRes={saveAssessmentRes}
          />

          <div className="card px-6 py-4 space-y-2">
            <h5>Summary</h5>
            <AssessmentSummaryView assessment={data.assessment} />
          </div>
        </div>
      </div>
    </AssessmentQuestionContext.Provider>
  );
};

export default AssessmentDetail;
